import React from 'react'
import { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { BlockQuote, Title } from '@ccpgames/eve-web-core'
import { Paragraph } from '@/components/shared/typography/Paragraph'
import type { Button as ButtonFieldsFragment } from '@/graphql/types'
import type RichTextType from '@/models/contentful/richText-models'
import { ButtonEntry } from '@/components/templates/dynamic/button'
import { renderEmbeddedAssets } from '@/components/shared/rich-text/renderAssets'
import { getRichTextItemsMap } from '@/components/shared/rich-text/getRichTextItemsMap'
import type { RichTextAssetType } from '@/components/shared/rich-text/types'
import { renderDlpTable, renderDlpTableCell, renderDlpTableHeaderCell, renderDlpTableRow } from './renderTable'
import styles from './richTextDlp.module.css'

const isButtonEntry = (entry: unknown): entry is ButtonFieldsFragment =>
  !!entry && (entry as { __typename?: string }).__typename === 'Button'

export const getRenderOptionsDlp = (document: RichTextType): Options => {
  const items = document.links
    ? getRichTextItemsMap(document.links)
    : {
        assets: new Map(),
        entries: new Map(),
      }

  return {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    },
    renderNode: {
      [BLOCKS.HEADING_3]: (_node, children) => <Title order={3}>{children}</Title>,
      [BLOCKS.HEADING_4]: (_node, children) => <Title order={4}>{children}</Title>,
      [BLOCKS.HEADING_5]: (_node, children) => <Title order={5}>{children}</Title>,
      [BLOCKS.HEADING_6]: (_node, children) => <Title order={6}>{children}</Title>,
      [BLOCKS.PARAGRAPH]: (_node, children) => (
        <Paragraph responsive={{ maxSize: 22, minSize: 18 }}>{children}</Paragraph>
      ),
      [BLOCKS.TABLE]: (node, children) => renderDlpTable(node, children),
      [BLOCKS.TABLE_ROW]: (_node, children) => renderDlpTableRow(children),
      [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => renderDlpTableHeaderCell(children),
      [BLOCKS.TABLE_CELL]: (_node, children) => renderDlpTableCell(children),
      [INLINES.HYPERLINK]: (node, children) => {
        const { uri } = node.data as { uri: string }
        if (!uri) return <>{children}</>
        return (
          <a className={styles.textLink} href={uri}>
            {children}
          </a>
        )
      },
      [BLOCKS.QUOTE]: (node, children) => {
        return <BlockQuote>{children}</BlockQuote>
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const sysId = node?.data?.target?.sys?.id
        if (!sysId) return null

        const asset = items?.assets?.get(sysId) as RichTextAssetType | undefined
        if (!asset) return null

        return renderEmbeddedAssets(asset, { decorateImages: false })
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const sysId = node?.data?.target?.sys?.id
        if (!sysId) return null

        const entry = items?.entries?.get(sysId)
        if (!entry) return null

        if (isButtonEntry(entry)) {
          if (!entry.sys?.id) return null
          return (
            <span className={styles.embeddedEntry}>
              <ButtonEntry entry={entry} />
            </span>
          )
        }

        // Only Button is supported for DLP embedded entries for now.
        return null
      },
    },
  }
}
